<template>
  <base-view>
    <div class="page-fill bg-gray-lighter">

      <!-- Hero -->
      <div
        class="relative w-full bg-center bg-cover"
        :style="{ backgroundImage: 'url(' + heroImage + ')' }"
      >
        <div class="absolute inset-0 hero-overlay"></div>
        <section class="relative flex flex-col items-center gap-3 max-w-3xl px-6 pt-16 pb-10 mx-auto text-center">
          <h1 class="m-0 text-4xl font-extrabold leading-tight text-gray-900">Forms &amp; Documents</h1>
          <p class="max-w-lg m-0 text-lg text-gray-700">
            Find the form, guide, or document you need.
          </p>
        </section>
      </div>

      <!-- Content -->
      <div class="max-w-4xl mx-auto px-6 py-8 pb-16">

        <!-- Label + inline filter + expand/collapse -->
        <div class="flex items-center gap-3 mb-4">
          <span class="text-xs font-bold tracking-wide text-gray-700 uppercase shrink-0" aria-live="polite">
            <template v-if="filterText.trim()">
              {{ totalFilteredCount }} result{{ totalFilteredCount !== 1 ? 's' : '' }}
            </template>
            <template v-else>Browse forms &amp; documents</template>
          </span>

          <div class="relative ml-auto">
            <input
              v-model="filterText"
              type="text"
              autocomplete="off"
              placeholder="Filter…"
              aria-label="Filter forms"
              class="box-border py-1.5 pl-7 pr-6 text-sm bg-white border border-gray-300 rounded-md outline-none focus:border-primary-light focus:ring-2 focus:ring-primary-light w-44"
            />
            <span class="filter-icon absolute left-2 text-gray-400 pointer-events-none">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </span>
            <button
              v-show="filterText"
              type="button"
              @click="filterText = ''"
              aria-label="Clear filter"
              class="filter-icon absolute right-2 text-gray-400 hover:text-gray-700 cursor-pointer"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <button
            v-show="!filterText.trim()"
            type="button"
            class="toggle-all-btn shrink-0"
            @click="toggleAll"
          >
            {{ allOpen ? 'Collapse all' : 'Expand all' }}
          </button>
        </div>

        <!-- Sections -->
        <div
          v-for="section in filteredSections"
          :key="section.title"
          class="form-section"
        >
          <button
            type="button"
            class="section-header"
            :aria-expanded="section.open.toString()"
            @click="toggleSection(section.title)"
          >
            <span class="section-accent" aria-hidden="true"></span>
            <h3 class="section-title">{{ section.title }}</h3>
            <span class="count-badge">
              {{ section.forms.length }}<template v-if="filterText.trim() && section.totalCount !== section.forms.length"> of {{ section.totalCount }}</template>
            </span>
            <span class="section-toggle" :class="{ open: section.open }">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>

          <transition @enter="enter" @after-enter="afterEnter" @leave="leave">
            <div v-show="section.open" class="section-body">
              <div class="flex flex-col md:flex-row">
                <div class="md:w-1/2">
                  <div
                    v-for="form in firstHalf(section.forms)"
                    :key="form.file"
                    class="form-row"
                  >
                    <span class="badge" :class="badgeClass(form.file)">{{ badgeLabel(form.file) }}</span>
                    <span class="form-title" v-html="highlight(form.title)"></span>
                    <router-link v-if="isRoute(form.file)" :to="form.file" class="form-cta">Open</router-link>
                    <a v-else :href="form.file" download class="form-cta">Download</a>
                  </div>
                </div>
                <div class="md:w-1/2 md:border-l border-gray-100">
                  <div
                    v-for="form in secondHalf(section.forms)"
                    :key="form.file"
                    class="form-row"
                  >
                    <span class="badge" :class="badgeClass(form.file)">{{ badgeLabel(form.file) }}</span>
                    <span class="form-title" v-html="highlight(form.title)"></span>
                    <router-link v-if="isRoute(form.file)" :to="form.file" class="form-cta">Open</router-link>
                    <a v-else :href="form.file" download class="form-cta">Download</a>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- No results -->
        <div v-if="filterText.trim() && filteredSections.length === 0" class="bg-white rounded-xl border border-gray-200 py-14 text-center px-6">
          <p class="text-sm font-semibold text-gray-700 mb-1">No forms match "{{ filterText.trim() }}"</p>
          <p class="text-sm text-gray-500">Try different keywords or <button type="button" class="text-primary font-semibold hover:underline" @click="filterText = ''">clear the filter</button>.</p>
        </div>

      </div>
    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";

export default {
  name: "FormsPortalHome",
  components: { BaseView },
  data() {
    return {
      heroImage: require("@/assets/bg.jpg"),
      filterText: "",
      sections: [
        {
          title: "Main Forms",
          open: true,
          forms: [
            { title: "Submit a Claim (Portal or App)", file: "/support-portal/articles/360018172533" },
            { title: "EAP Application", file: "EAP Application.pdf" },
            { title: "FSA Expense Planning Worksheet", file: "FSA Expense Planning Worksheet.pdf" },
            { title: "Letter of Medical Necessity", file: "Letter of Medical Necessity.pdf" },
            { title: "LPF to FSA Conversion", file: "LPF to FSA Conversion.pdf" },
            { title: "Employee Guide to COBRA", file: "Employee Guide to COBRA.pdf" },
            { title: "Mileage Worksheet", file: "Mileage Worksheet.xlsx" }
          ]
        },
        {
          title: "Plan Guides",
          open: true,
          forms: [
            { title: "RMR FSA User Guide", file: "2026 Health FSA User Guide.pdf" },
            { title: "RMR DCA User Guide", file: "2026 Dependent Care FSA User Guide.pdf" },
            { title: "RMR PKG User Guide", file: "2026 Qualified Parking User Guide.pdf" },
            { title: "RMR TRN User Guide", file: "2026 Qualified Transit User Guide.pdf" },
            { title: "RMR HSA User Guide", file: "2027 HSA User Guide.pdf" },
            { title: "RMR Limited FSA User Guide", file: "2026 Limited Purpose Health FSA User Guide.pdf" },
            { title: "RMR FSA (Health & Dependent Care) User Guide", file: "2026 FSA (Health & Dependent Care) User Guide.pdf" },
            { title: "RMR FSA (Health, Limited, & Dependent Care) User Guide", file: "2026 FSA (Health, Limited, & Dependent Care) User Guide.pdf" },
            { title: "RMR ICHRA Enrollment Guide", file: "RMR ICHRA Enrollment Guide.pdf" },
            { title: "RMR FSA No LPF Enrollment Guide", file: "RMR FSA No LPF Enrollment Guide.pdf" },
            { title: "RMR LSA Enrollment Guide", file: "RMR LSA Enrollment Guide.pdf" },
            { title: "RMR HRA Enrollment Guide", file: "RMR HRA Enrollment Guide.pdf" },
            { title: "RMR HRA Enrollment Guide No Debit Card", file: "RMR HRA Enrollment Guide No Debit Card.pdf" }
          ]
        },
        {
          title: "HSA Forms",
          open: true,
          forms: [
            { title: "HSA Transfer Form from Prior Bank", file: "Consolidate HSAs - External to Elevate Form.pdf" },
            { title: "Death Distribution Form", file: "Death Distribution.pdf" },
            { title: "HSA Mistaken Distribution Form", file: "Elevate HSA Mistaken Distribution Form.pdf" },
            { title: "HSA Rollover Form", file: "Elevate HSA Rollover Form.pdf" },
            { title: "HSA Transfer Divorce Form", file: "HSA Transfer Divorce.pdf" },
            { title: "HSA Withdrawal Request", file: "/support-portal/articles/45911104934803" },
            { title: "Withdraw HSA Funds (Portal or App)", file: "/support-portal/articles/45911104934803-Withdrawing-Funds-or-Closing-Your-HSA" },
            { title: "Consolidate HSA Funds", file: "/support-portal/articles/53577887508627" },
            { title: "Interest Waiver", file: "Interest Waiver.pdf" },
            { title: "Power of Attorney Form", file: "Power of Attorney.pdf" },
            { title: "HSA Name Change", file: "HSA_Name_Change.pdf" }
          ]
        },
        {
          title: "Español / Spanish",
          open: false,
          forms: [
            { title: "Guía del usuario de la FSA", file: "Spanish 2026 Health FSA User Guide.pdf" },
            { title: "Guía del usuario de HSA", file: "Spanish 2027 HSA User Guide.pdf" },
            { title: "Guía del usuario del DCA", file: "Spanish 2026 Dependent Care FSA User Guide.pdf" },
            { title: "Guía del usuario de FSA (Cuenta de Salud y Cuidado de Dependientes)", file: "Spanish 2026 FSA (Health & Dependent Care) User Guide.pdf" },
            { title: "Guía del usuario de la FSA de propósito limitado", file: "Spanish 2026 Limited Purpose Health FSA User Guide.pdf" },
            { title: "RMR PKG & TRN Guía de Inscripcion", file: "RMR PKG & TRN Guía de Inscripcion.pdf" },
            { title: "RMR Formulario de Reclamación", file: "RMR Formulario de Reclamación.pdf" },
            { title: "RMR Formulario de Inscripción", file: "RMR Formulario de Inscripción.pdf" },
            { title: "Formulario de Inscripción (HSA)", file: "Formulario de Inscripción (HSA).pdf" },
            { title: "Formulario para Depósito Directo", file: "Formulario para Depósito Directo.pdf" },
            { title: "RMR Solicitud de Tarjeta de Débito Adicional", file: "RMR Solicitud de Tarjeta de Débito Adicional.pdf" }
          ]
        }
      ]
    };
  },
  computed: {
    filteredSections() {
      const q = this.filterText.trim().toLowerCase();
      if (!q) return this.sections;
      return this.sections
        .map(s => {
          const matched = s.forms.filter(f => f.title.toLowerCase().includes(q));
          return { ...s, open: true, forms: matched, totalCount: s.forms.length };
        })
        .filter(s => s.forms.length > 0);
    },
    allOpen() {
      return this.sections.every(s => s.open);
    },
    totalFilteredCount() {
      return this.filteredSections.reduce((sum, s) => sum + s.forms.length, 0);
    }
  },
  methods: {
    toggleSection(title) {
      if (this.filterText.trim()) return;
      const s = this.sections.find(s => s.title === title);
      if (s) s.open = !s.open;
    },
    toggleAll() {
      const target = !this.allOpen;
      this.sections.forEach(s => { s.open = target; });
    },
    highlight(text) {
      const q = this.filterText.trim();
      if (!q) return text;
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return text.replace(
        new RegExp(`(${escaped})`, "gi"),
        '<mark class="highlight">$1</mark>'
      );
    },
    firstHalf(forms) {
      return forms.slice(0, Math.ceil(forms.length / 2));
    },
    secondHalf(forms) {
      return forms.slice(Math.ceil(forms.length / 2));
    },
    isRoute(file) {
      const ext = file.split(".").pop().toLowerCase();
      return file.startsWith("/") && !["pdf", "xlsx", "doc", "docx"].includes(ext);
    },
    fileExt(file) {
      return file.split(".").pop().toLowerCase();
    },
    badgeLabel(file) {
      if (this.isRoute(file)) return "Article";
      return this.fileExt(file) === "xlsx" ? "XLS" : "PDF";
    },
    badgeClass(file) {
      if (this.isRoute(file)) return "badge-article";
      return this.fileExt(file) === "xlsx" ? "badge-xls" : "badge-pdf";
    },
    enter(element) {
      const width = getComputedStyle(element).width;
      element.style.width = width;
      element.style.position = "absolute";
      element.style.visibility = "hidden";
      element.style.height = "auto";
      const height = getComputedStyle(element).height;
      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;
      getComputedStyle(element).height;
      const duration = (Math.floor(parseInt(height) / 100) + 1) * 0.2;
      setTimeout(() => {
        element.style.transition = `height ${duration}s ease-in-out`;
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
    leave(element) {
      const height = getComputedStyle(element).height;
      element.style.height = height;
      getComputedStyle(element).height;
      const duration = Math.floor(parseInt(height) / 100) * 0.15;
      setTimeout(() => {
        element.style.transition = `height ${duration}s ease-in-out`;
        element.style.height = 0;
      });
    }
  }
};
</script>

<style scoped>
.page-fill { min-height: calc(100vh - 10rem); }

/* Hero */
.hero-overlay {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.62) 0%,
    rgba(255, 255, 255, 0.88) 45%,
    #fafafa 75%,
    #fafafa 100%
  );
}

/* Filter input */
.filter-icon {
  top: 50%;
  transform: translateY(-50%);
}

/* Expand/collapse all */
.toggle-all-btn {
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: #096843;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  outline: none;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.toggle-all-btn:hover { color: #1c7a4d; }
.toggle-all-btn:focus-visible { outline: 2px solid #096843; outline-offset: 2px; border-radius: 2px; }

/* Section cards */
.form-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: 1rem;
}

.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  background: #f9fafb;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  text-align: left;
  outline: none;
  transition: background 0.15s;
}
.section-header:hover { background: #f3f4f6; }
.section-header:focus-visible { box-shadow: inset 0 0 0 2px #096843; }

.section-accent {
  display: block;
  width: 4px;
  height: 1.375rem;
  border-radius: 9999px;
  background: #096843;
  flex-shrink: 0;
}

.section-title {
  flex: 1;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.count-badge {
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 9999px;
  padding: 0.125rem 0.625rem;
  flex-shrink: 0;
}

.section-toggle {
  color: #9ca3af;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}
.section-toggle.open { transform: rotate(180deg); }

.section-body {
  padding: 0;
  overflow: hidden;
  background: #ffffff;
}

/* Form rows */
.form-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
}
.form-row:last-child { border-bottom: none; }
.form-row:hover { background: #f9fafb; }

.badge {
  flex-shrink: 0;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.badge-pdf     { background: #fef2f2; color: #b91c1c; }
.badge-xls     { background: #f0fdf4; color: #15803d; }
.badge-article { background: #eff6ff; color: #1d4ed8; }

.form-title {
  flex: 1;
  font-size: 0.9375rem;
  color: #111827;
  line-height: 1.4;
}

.form-cta {
  flex-shrink: 0;
  padding: 0.3rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  background: #096843;
  color: #ffffff;
  border: none;
  transition: background 0.15s;
}
.form-cta:hover { background: #1c7a4d; }
</style>

<style>
/* Unscoped so v-html highlight marks render correctly */
.highlight {
  background-color: #fef08a;
  color: #111827;
  border-radius: 2px;
  padding: 0 1px;
}
</style>
